import * as React from 'react';

import { TableDensityContext, TableHeaderContext, useTableDensity } from '@/components/ui/table-context';
import { cn } from '@/lib/utils';

import { TABLE_CELL_ALIGN, TABLE_CELL_KIND, TABLE_DENSITY } from './types/table-variants-enums';







function TableDensityProvider({
  density,
  children,
}: {
  density?: TABLE_DENSITY;
  children: React.ReactNode;
}) {
  return (
    <TableDensityContext.Provider value={density}>{children}</TableDensityContext.Provider>
  );
}

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  shouldHideOverflow?: boolean;
  density?: TABLE_DENSITY;
}

const Table = React.forwardRef<
  HTMLTableElement,
  TableProps
>(({ className, shouldHideOverflow = false, density, ...props }, ref) => {
  return (
    <TableDensityContext.Provider value={density}>
      <div
        className={cn('relative w-full overflow-auto', {
          'overflow-hidden': shouldHideOverflow,
        })}
      >
        <table ref={ref} className={cn('w-full caption-bottom text-sm', className)} {...props} />
      </div>
    </TableDensityContext.Provider>
  );
});
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <TableHeaderContext.Provider value>
    <thead ref={ref} className={cn('[&_tr]:border-b', className)} {...props} />
  </TableHeaderContext.Provider>
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn('[&_tr:last-child]:border-0', className)} {...props} />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn('border-t bg-card font-medium [&>tr]:last:border-b-0', className)}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  interactive?: boolean;
  actionable?: boolean;
}

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  (
    {
      className,
      interactive = false,
      actionable = false,
      onClick,
      onKeyDown,
      tabIndex,
      ...props
    },
    ref
  ) => {
    const density = useTableDensity();
    const isHeaderRow = React.useContext(TableHeaderContext);
    const isActivatable = interactive && !!onClick;
    const isDensityOptedIn = density !== undefined;

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
      onKeyDown?.(event);
      if (event.defaultPrevented || !isActivatable) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick?.(event as unknown as React.MouseEvent<HTMLTableRowElement>);
      }
    };

    return (
      <tr
        ref={ref}
        data-interactive={interactive || undefined}
        data-actionable={actionable || undefined}
        className={cn(
          // Phase 7: omit density keeps legacy geometry; selected wash is always neutral.
          'border-b transition-colors data-[state=selected]:bg-neutral-interaction-selected',
          density === TABLE_DENSITY.COMPACT && !isHeaderRow && 'h-11',
          density === TABLE_DENSITY.DEFAULT && !isHeaderRow && 'h-12',
          isDensityOptedIn &&
            (actionable || interactive) &&
            'hover:bg-neutral-interaction-hover',
          !isDensityOptedIn &&
            interactive &&
            'hover:bg-accent hover:text-accent-foreground',
          isActivatable && 'cursor-pointer',
          className
        )}
        onClick={onClick}
        onKeyDown={isActivatable ? handleKeyDown : onKeyDown}
        tabIndex={isActivatable ? (tabIndex ?? 0) : tabIndex}
        {...props}
      />
    );
  }
);
TableRow.displayName = 'TableRow';

interface TableCellContractProps {
  align?: TABLE_CELL_ALIGN;
  kind?: TABLE_CELL_KIND;
}

interface TableHeadProps
  extends Omit<React.ThHTMLAttributes<HTMLTableCellElement>, 'align'>,
    TableCellContractProps {}

interface TableCellProps
  extends Omit<React.TdHTMLAttributes<HTMLTableCellElement>, 'align'>,
    TableCellContractProps {}

function getCellAlignmentClassName(
  align: TABLE_CELL_ALIGN | undefined,
  kind: TABLE_CELL_KIND | undefined
): string | undefined {
  if (kind === TABLE_CELL_KIND.NUMERIC || kind === TABLE_CELL_KIND.ACTION) {
    return 'text-right';
  }
  if (align === TABLE_CELL_ALIGN.START) {
    return 'text-left';
  }
  if (align === TABLE_CELL_ALIGN.CENTER) {
    return 'text-center';
  }
  if (align === TABLE_CELL_ALIGN.END) {
    return 'text-right';
  }
  return undefined;
}

function getCellKindClassName(kind: TABLE_CELL_KIND | undefined): string | undefined {
  if (kind === TABLE_CELL_KIND.NUMERIC) {
    return 'tabular-nums';
  }
  if (kind === TABLE_CELL_KIND.STATUS) {
    return 'whitespace-nowrap';
  }
  if (kind === TABLE_CELL_KIND.ACTION) {
    return 'w-px whitespace-nowrap';
  }
  return undefined;
}

const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, align, kind, ...props }, ref) => {
    const density = useTableDensity();

    return (
      <th
        ref={ref}
        className={cn(
          density === undefined &&
            'h-12 px-4 text-left align-middle font-medium text-foreground [&:has([role=checkbox])]:pr-0',
          density === TABLE_DENSITY.COMPACT &&
            'h-9 px-3 text-left align-middle type-table-header font-medium text-content-secondary [&:has([role=checkbox])]:pr-0',
          density === TABLE_DENSITY.DEFAULT &&
            'h-10 px-4 text-left align-middle type-table-header font-medium text-content-secondary [&:has([role=checkbox])]:pr-0',
          getCellAlignmentClassName(align, kind),
          getCellKindClassName(kind),
          className
        )}
        {...props}
      />
    );
  }
);
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, align, kind, ...props }, ref) => {
    const density = useTableDensity();

    return (
      <td
        ref={ref}
        className={cn(
          density === undefined && 'p-4 align-middle [&:has([role=checkbox])]:pr-0',
          density === TABLE_DENSITY.COMPACT &&
            'px-3 py-2 align-middle type-field-label [&:has([role=checkbox])]:pr-0',
          density === TABLE_DENSITY.DEFAULT &&
            'px-4 py-2.5 align-middle type-body [&:has([role=checkbox])]:pr-0',
          getCellAlignmentClassName(align, kind),
          getCellKindClassName(kind),
          className
        )}
        {...props}
      />
    );
  }
);
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption ref={ref} className={cn('mt-4 text-sm text-muted-foreground', className)} {...props} />
));
TableCaption.displayName = 'TableCaption';

export { Table, TableBody, TableCaption, TableCell, TableDensityProvider, TableFooter, TableHead, TableHeader, TableRow };
